<template>
  <div>
    <NuxtLoadingIndicator color="black" />
    <LazyConsentBanner v-if="cookieBannerVisible" />
    <ConsentModal />
    <div
      v-if="
        isFeatureEnabled('showRedirectToBrowserLanguageBanner') &&
        isUpdateLanguageBarShown
      "
      class="w-full bg-black py-5 print:hidden"
    >
      <div class="container flex justify-between gap-2 text-white">
        <div class="flex items-center gap-2">
          <p class="font-pragmatica-ext font-bold">
            <span v-if="browserLanguage === 'cs' || browserLanguage === 'sk'">
              Vypadá to, že pro vás máme vhodnější jazykovou verzi. Chcete
              přepnout do češtiny?
            </span>
            <span v-else-if="browserLanguage === 'fr'">
              Selon votre région, nous vous proposons l’affichage en français.
              Acceptez-vous cette version linguistique ?
            </span>
            <span v-else-if="browserLanguage === 'de'">
              Abhängig von Ihrem Standort bieten wir Ihnen die Website auf
              Deutsch an. Diese Version anzeigen?
            </span>
            <span v-else>
              We are offering our website in English, based on your location.
              Display this version?
            </span>
          </p>
          <NuxtLink
            :to="
              $localePath(
                { path: route.path, hash: route.hash, query: route.query },
                ['cs', 'fr', 'de', 'en'].includes(browserLanguage)
                  ? (browserLanguage as 'cs' | 'fr' | 'de' | 'en')
                  : 'en'
              )
            "
          >
            <p class="font-pragmatica-ext font-bold">
              <span v-if="browserLanguage === 'cs' || browserLanguage === 'sk'">
                Přepnout
              </span>
              <span v-else-if="browserLanguage === 'fr'">Afficher</span>
              <span v-else-if="browserLanguage === 'de'">Anzeigen</span>
              <span v-else>Display</span>
            </p>
          </NuxtLink>
        </div>
        <button
          class="font-pragmatica-ext font-bold underline"
          @click="hideLanguageBar()"
        >
          <IconClose invert />
        </button>
      </div>
    </div>
    <UNotifications
      :ui="{
        position: 'top-0 !z-[105] bottom-[unset] left-1/2 -translate-x-1/2',
      }"
    />
    <NotificationNotifications />
    <UiRegisterNotification />
    <UiAlert
      :show="store.isVisible"
      :messages="store.messages"
      @close="store.setVisible(false)"
    />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { useAlertsStore } from '~/stores/alerts'

const { isFeatureEnabled } = useFeatureFlags()

const route = useRoute()

const { t } = useI18n()

const { isUpdateLanguageBarShown, browserLanguage, hideLanguageBar } =
  useUpdateLanguage()

const head = useLocaleHead()

useServerHead({
  htmlAttrs: head.value.htmlAttrs,
  link: head.value.link,
  meta: head.value.meta,
})

useScripts()
useScrollControl()

const store = useAlertsStore()
const { defaultOgImage } = useOgImage()

useSeoMeta({
  title: 'LD Seating',
  description: t('pages.index.meta.description'),
  ogImage: defaultOgImage,
})

const cookieBannerVisible = ref(false)

onMounted(() => {
  // TODO: Drop in future
  localStorage['nuxt-color-mode'] = 'light'
  cookieBannerVisible.value = true
})
</script>

<style lang="scss">
::selection {
  background-color: #000;
  color: #fff;
}

.bg-black {
  ::selection {
    background-color: #fff;
    color: #000;
  }
}

@font-face {
  font-family: 'Pragmatica';
  src:
    local('Pragmatica Book'),
    local('Pragmatica-Book'),
    url('/fonts/pragmatica-book.woff2') format('woff2'),
    url('/fonts/pragmatika-book.woff') format('woff');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Pragmatica';
  src:
    local('Pragmatica Bold'),
    local('Pragmatica-Bold'),
    url('/fonts/pragmatica-bold.woff2') format('woff2'),
    url('/fonts/pragmatika-bold.woff') format('woff');
  font-weight: bold;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Pragmatica Ext';
  src:
    local('Pragmatica Ext Book'),
    local('PragmaticaExt-Book'),
    url('/fonts/pragmatica-ext-book.woff2') format('woff2'),
    url('/fonts/pragmatica-ext-book.woff') format('woff');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Pragmatica Ext';
  src:
    local('Pragmatica Ext Bold'),
    local('PragmaticaExt-Bold'),
    url('/fonts/pragmatica-ext-bold.woff2') format('woff2'),
    url('/fonts/pragmatica-ext-bold.woff') format('woff');
  font-weight: bold;
  font-style: normal;
  font-display: swap;
}

.sl-overlay {
  background-color: black !important;
}

.sl-wrapper .sl-close {
  color: white !important;
}

.sl-wrapper .sl-navigation button {
  color: white !important;
}
</style>
